import React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest, fetchUserInfo, logout } from '../../utils';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import { Link } from 'react-router-dom';
import Image from '../../components/images/Image';
import useDataSaver, { setDataSaver } from '../../hooks/useDataSaver';
import ProfileCompleteness from '../../components/common/ProfileCompleteness';

const ProfileRow = ({ to, onClick, icon, label, tone = 'default', external = false }) => {
  const inner = (
    <div className="profile-row">
      <div className={`profile-row__icon profile-row__icon--${tone}`}>
        <Icon name={icon} />
      </div>
      <div className="profile-row__label">{label}</div>
      <Icon name="angle-right" className="profile-row__chevron" />
    </div>
  );

  if (external) {
    return (
      <a href={to} className="profile-row__link" onClick={onClick}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className="profile-row__link" onClick={onClick}>
      {inner}
    </Link>
  );
};

// Like ProfileRow but toggles in place instead of navigating. Used for the
// data-saver switch — the right-hand chevron is replaced by an on/off state.
const ProfileToggleRow = ({ icon, label, tone = 'default', value, onToggle }) => {
  const { t } = useTranslation();
  return (
    <div
      className="profile-row__link"
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div className="profile-row">
        <div className={`profile-row__icon profile-row__icon--${tone}`}>
          <Icon name={icon} />
        </div>
        <div className="profile-row__label">{label}</div>
        <span className="profile-row__chevron" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
          {value ? t('on') : t('off')}
          <Icon name={value ? 'check-circle' : 'cross-circle'} theme={value ? 'primary' : 'secondary'} />
        </span>
      </div>
    </div>
  );
};

const ProfileSection = ({ title, children }) => (
  <div className="profile-section mx-3 mb-3">
    {title && <div className="profile-section__title">{title}</div>}
    <Card mb="0" mx="0" p="0" className="profile-section__card">
      {children}
    </Card>
  </div>
);

const Profile = () => {
  const { t } = useTranslation();
  const dataSaver = useDataSaver();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/profile', setUserInfo, setLoading, setError);
  }, []);

  // My prizes only for somebody who won something in a raffle — an empty page is not a menu item.
  const [hasPrizes, setHasPrizes] = useState(false);
  useEffect(() => {
    apiRequest('GET', '/my-prizes').then((d) => setHasPrizes((d?.prizes || []).length > 0)).catch(() => {});
  }, []);

  return (
    <Screen>
      <div className="top">
        <div className="profile-hero mx-3 mt-3 mb-3">
          <div className="profile-hero__bg" aria-hidden="true" />
          <div className="profile-hero__inner">
            <Image
              url={userInfo?.picture ? userInfo.picture : undefined}
              className="profile-hero__avatar circle"
              reload={true}
            />
            <h3 className="profile-hero__name">
              {loading ? (
                <span className="placeholder-glow"><span className="placeholder col-6"></span></span>
              ) : (
                userInfo?.full_name
              )}
            </h3>
            <p className="profile-hero__email">
              {loading ? (
                <span className="placeholder-glow"><span className="placeholder col-4"></span></span>
              ) : (
                userInfo?.email
              )}
            </p>
            {userInfo?.role && (
              <span className="profile-hero__role">
                <Icon name="user" theme="light" className="me-1" />
                {t(userInfo.role)}
              </span>
            )}
          </div>
        </div>

        {/* Directly under the hero, above the rows: this page is where somebody comes to look at
            themselves, so "how much of me is filled in" belongs at the top of it — and the row it
            sends them to, Edit profile, is the very next thing on the screen. Unlike the ring on
            the member home this keeps its finished state; here the answer is what was asked. */}
        <div className="mx-3 mb-3">
          <ProfileCompleteness variant="card" />
        </div>

        <ProfileSection title={t('account')}>
          <ProfileRow
            to="/wallet"
            icon="wallet"
            label={t('wallet')}
            tone="primary"
          />
          {/* Shown to everybody rather than probed for like My prizes: finding out whether there
              is anything to list costs the same reads as listing it, and an empty billing page
              ("no invoices yet") is an answer, not a dead end. */}
          <ProfileRow
            to="/billing"
            icon="file-invoice"
            label={t('billing')}
            tone="info"
          />
          <ProfileRow
            to="/edit-profile"
            icon="user-pen"
            label={t('editProfile')}
            tone="primary"
          />
          {/* Directly under Edit profile, not down in Preferences where it used to sit between
              Language and Data saver. Filed as a setting it reads as one, and nobody found it —
              these are the links that let colleagues actually reach you, which is a part of the
              profile, not a preference about the app. */}
          <ProfileRow
            to="/connections"
            icon="share-square"
            label={t('myLinks')}
            tone="pink"
          />
          {/* ★ The ONLY way most people reach My Network. The member home carries a card for it,
              but that home is Bravos — a Hercules Trophy or Fabrica player collects networking-QR
              contacts just the same and never sees it. The bottom nav has three buttons, so the
              profile is the one screen reachable from everywhere. Sits next to "My links"
              deliberately: those are the details handed over, this is who received them. */}
          <ProfileRow
            to="/my-network"
            icon="users"
            label={t('myNetwork')}
            tone="primary"
          />
          {/* Badges are granted at night and kept for life — the bell opens /badges, and this is
              the way back to it for everybody, Hercules player and Bravos member alike. */}
          <ProfileRow
            to="/badges"
            icon="award"
            label={t('badges')}
            tone="primary"
          />
          {hasPrizes && (
            <ProfileRow
              to="/my-prizes"
              icon="gift"
              label={t('myPrizes')}
              tone="pink"
            />
          )}
          <ProfileRow
            to="/change-email"
            icon="envelope"
            label={t('changeEmail')}
            tone="info"
          />
          <ProfileRow
            to="/change-password"
            icon="lock"
            label={t('changePassword')}
            tone="purple"
          />
          {userInfo?.is_public && (
            <ProfileRow
              to={`/u/${userInfo.id}`}
              icon="eye"
              label={t('publicProfile')}
              tone="info"
            />
          )}
          <ProfileRow
            to="/role"
            icon="shuffle"
            label={t('selectYourRole')}
            tone="purple"
          />
        </ProfileSection>

        <ProfileSection title={t('preferences')}>
          <ProfileRow
            to="/language"
            icon="language-exchange"
            label={t('language')}
            tone="info"
          />
          <ProfileToggleRow
            icon="picture"
            label={t('dataSaver')}
            tone="purple"
            value={dataSaver}
            onToggle={() => setDataSaver(!dataSaver)}
          />
        </ProfileSection>
        <p className="text-center mb-3" style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0 1.4rem' }}>
          {t('dataSaverWarning')}
        </p>

        <ProfileSection title={t('support')}>
          <ProfileRow
            to="mailto:info@herculestrophy.com"
            icon="customer-service"
            label={t('contactUs')}
            tone="primary"
            external
          />
        </ProfileSection>

        <div className="px-3 mb-2">
          <Link
            to="/login"
            onClick={(e) => { e.preventDefault(); logout(); }}
            className="profile-logout"
          >
            <Icon name="sign-out-alt" theme="primary" className="me-2" />
            <strong>{t('logout')}</strong>
          </Link>
        </div>

        <p className="profile-legal text-center mb-5">
          <Link to="/privacy" className="profile-legal__link">
            {t('privacyPolicy', { defaultValue: 'Privacy Policy' })}
          </Link>
          {' · '}
          <Link to="/terms" className="profile-legal__link">
            {t('termsOfService', { defaultValue: 'Terms' })}
          </Link>
        </p>
      </div>

      <div className="bottom">
        <NavBar active="profile" />
      </div>
    </Screen>
  );
};

export default Profile;
